import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  CYBER_NODE_KINDS,
  CyberNodeKind,
  CyberTopologyDraft,
  CyberTopologyNode,
} from '../../../models/cyber-topology.model';
import {
  AUTHOR_UNIT,
  LEAF_R,
  NODE_H,
  NODE_LINE_H,
  NODE_SUB_GAP,
  NODE_W,
  VIEW_H,
  VIEW_W,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
  fixedTransform,
  nodeHeight,
  nodeWidth,
  wrapNodeLabel,
} from '../state/topology-layout';

/**
 * Draws and edits a network topology.
 *
 * Everything is direct manipulation on the same SVG the page renders: drop a kind onto the
 * canvas to add a node, drag it to move it, click two nodes in link mode to connect them.
 * The whole graph is held locally and submitted in one go, so Cancel really does cancel.
 */

/**
 * Slack kept around the drawing, in viewBox units, so there is always somewhere further to
 * drag a node to. Roughly one node wide.
 */
const CANVAS_MARGIN = NODE_W;

/** How close to the viewport edge a drag has to get before the canvas follows it, in px. */
const AUTOSCROLL_MARGIN = 44;
/** Hard cap on one step, so a pointer parked in the corner scrolls smoothly, not violently. */
const AUTOSCROLL_MAX_STEP = 18;

interface RenderedNode {
  node: CyberTopologyNode;
  /** The label broken into the lines the box draws. */
  lines: string[];
  /** Baseline of the first line, so the block sits centred however many lines there are. */
  firstLineY: number;
  cx: number;
  cy: number;
  x: number;
  y: number;
  /** Box size, fitted to the label in both directions. */
  w: number;
  h: number;
  /** Subtitle baseline, which moves with the box height. */
  subY: number;
  isLeaf: boolean;
}

interface RenderedEdge {
  key: string;
  index: number;
  sourceKey: string;
  targetKey: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  proto: string | null;
}

@Component({
  selector: 'app-topology-editor',
  standalone: true,
  imports: [],
  templateUrl: './topology-editor.html',
  styleUrl: './topology-editor.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopologyEditorComponent {
  /** The graph to edit. The editor copies it, so the caller's object is never mutated. */
  draft = input.required<CyberTopologyDraft>();
  saving = input<boolean>(false);
  error = input<string | null>(null);

  saved = output<CyberTopologyDraft>();
  cancelled = output<void>();

  readonly leafR = LEAF_R;
  readonly lineH = NODE_LINE_H;
  readonly kinds = CYBER_NODE_KINDS;

  private host = inject(ElementRef<HTMLElement>);
  private svgEl = viewChild<ElementRef<SVGSVGElement>>('svgEl');
  private wrapEl = viewChild<ElementRef<HTMLElement>>('wrapEl');

  // Local working copies. `linkedSignal` is what makes Cancel real: the editor mutates
  // these freely, and opening a different topology resets them from the new input rather
  // than carrying the abandoned edits over.
  name = linkedSignal(() => this.draft().name);
  description = linkedSignal(() => this.draft().description ?? '');
  nodes = linkedSignal(() =>
    this.draft().nodes.map((node) => ({ ...node, aliases: [...node.aliases] })),
  );
  edges = linkedSignal(() => this.draft().edges.map((edge) => ({ ...edge })));

  /** Node key currently selected for the side form, or null. */
  selectedKey = linkedSignal<CyberTopologyDraft, string | null>({
    source: this.draft,
    computation: () => null,
  });
  /** In link mode, the first node clicked; the second click completes the edge. */
  linkFrom = signal<string | null>(null);
  linkMode = signal(false);
  /** Why a click in link mode was refused, shown alongside the link hint. */
  linkNotice = signal<string | null>(null);
  validationError = signal<string | null>(null);

  zoom = signal(ZOOM_MIN);
  canZoomIn = computed(() => this.zoom() < ZOOM_MAX);
  canZoomOut = computed(() => this.zoom() > ZOOM_MIN);

  private dragKey: string | null = null;
  /**
   * Where inside the node the drag started, in author units. Without it a node jumps so its
   * centre sits under the cursor, which reads as the diagram fighting back.
   */
  private dragOffset = { x: 0, y: 0 };
  private newNodeSeq = 0;

  // Normalising against the live node set would make every node shift as one is dragged, and
  // would rescale the drawing every time the canvas grew. A constant scale does neither.
  private readonly transform = fixedTransform();

  /**
   * Size of the canvas element. The window shown of the drawing is given this same shape, so
   * the drawing fills the element instead of being letterboxed inside it — the dead bands a
   * mismatched aspect leaves behind are canvas the user can see but cannot drag onto.
   */
  private viewport = signal({ w: VIEW_W, h: VIEW_H });

  /** The window shown when the drawing fits: the element's shape, at the drawing's scale. */
  private baseBox = computed(() => {
    const { w, h } = this.viewport();
    const aspect = h > 0 ? w / h : VIEW_W / VIEW_H;
    return { w: VIEW_H * aspect, h: VIEW_H };
  });

  /**
   * The window actually shown, in viewBox units: the base window, extended in whatever
   * direction the drawing has outgrown it. Growing this adds room; it never moves a node.
   */
  private canvasBox = computed(() => {
    const base = this.baseBox();
    const { tx, ty } = this.transform;

    let minX = 0;
    let minY = 0;
    let maxX = base.w;
    let maxY = base.h;

    for (const node of this.nodes()) {
      const cx = tx(node.x);
      const cy = ty(node.y);
      // Each node's own half-extent: a box widened or heightened for its label has to pull the
      // canvas with it when dragged to an edge, or it ends up clipped outside the scrollable area.
      const halfW = node.kind === 'leaf' ? LEAF_R : nodeWidth(node.label) / 2;
      const halfH = node.kind === 'leaf' ? LEAF_R : nodeHeight(node.label) / 2;
      minX = Math.min(minX, cx - halfW - CANVAS_MARGIN);
      minY = Math.min(minY, cy - halfH - CANVAS_MARGIN);
      maxX = Math.max(maxX, cx + halfW + CANVAS_MARGIN);
      maxY = Math.max(maxY, cy + halfH + CANVAS_MARGIN);
    }

    return { minX, minY, w: maxX - minX, h: maxY - minY };
  });

  viewBox = computed(() => {
    const { minX, minY, w, h } = this.canvasBox();
    return `${round2(minX)} ${round2(minY)} ${round2(w)} ${round2(h)}`;
  });

  // Percentages of the canvas element: exactly 100% while the drawing fits, more once it has
  // outgrown it — which is what puts the viewport into scroll.
  canvasWidthPct = computed(() => (this.canvasBox().w / this.baseBox().w) * 100 * this.zoom());
  canvasHeightPct = computed(() => (this.canvasBox().h / this.baseBox().h) * 100 * this.zoom());

  constructor() {
    const destroyRef = inject(DestroyRef);

    afterNextRender(() => {
      const wrap = this.wrapEl()?.nativeElement;
      if (!wrap || typeof ResizeObserver === 'undefined') return;

      // Measured border-box: the content box shrinks when a scrollbar appears, which would
      // feed back into the size that decided whether to show one.
      const observer = new ResizeObserver(() => {
        const { width, height } = wrap.getBoundingClientRect();
        if (width > 0 && height > 0) this.viewport.set({ w: width, h: height });
      });
      observer.observe(wrap);
      destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  renderedNodes = computed<RenderedNode[]>(() => {
    const { tx, ty } = this.transform;
    return this.nodes().map((node) => {
      const cx = tx(node.x);
      const cy = ty(node.y);
      const w = nodeWidth(node.label);
      const h = nodeHeight(node.label);
      const lines = wrapNodeLabel(node.label);
      return {
        node,
        lines,
        // The block is centred on the box, so a single-line node keeps the baseline it has today.
        firstLineY: cy + 0.2 - ((lines.length - 1) * NODE_LINE_H) / 2,
        cx,
        cy,
        x: cx - w / 2,
        y: cy - h / 2,
        w,
        h,
        subY: cy + h / 2 + NODE_SUB_GAP,
        isLeaf: node.kind === 'leaf',
      };
    });
  });

  renderedEdges = computed<RenderedEdge[]>(() => {
    const { tx, ty } = this.transform;
    const byKey = new Map(this.nodes().map((n) => [n.nodeKey, n]));

    return this.edges()
      .map((edge, index) => {
        const a = byKey.get(edge.sourceKey);
        const b = byKey.get(edge.targetKey);
        if (!a || !b) return null;
        return {
          key: `${edge.sourceKey}→${edge.targetKey}`,
          index,
          sourceKey: edge.sourceKey,
          targetKey: edge.targetKey,
          x1: tx(a.x),
          y1: ty(a.y),
          x2: tx(b.x),
          y2: ty(b.y),
          proto: edge.proto,
        };
      })
      .filter((edge): edge is RenderedEdge => edge !== null);
  });

  selected = computed<CyberTopologyNode | null>(() => {
    const key = this.selectedKey();
    return this.nodes().find((node) => node.nodeKey === key) ?? null;
  });

  /** Candidate parents for a leaf: every node that is not itself a leaf. */
  parentOptions = computed(() =>
    this.nodes().filter((node) => node.kind !== 'leaf' && node.nodeKey !== this.selectedKey()),
  );

  // ── Palette and canvas ───────────────────────────────────────────────────

  onPaletteDragStart(event: DragEvent, kind: CyberNodeKind): void {
    event.dataTransfer?.setData('text/plain', kind);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
  }

  onCanvasDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onCanvasDrop(event: DragEvent): void {
    event.preventDefault();
    const kind = event.dataTransfer?.getData('text/plain') as CyberNodeKind | undefined;
    if (!kind || !CYBER_NODE_KINDS.includes(kind)) return;

    const point = this.toAuthorSpace(event);
    if (!point) return;

    const nodeKey = this.uniqueKey(kind);
    this.nodes.update((nodes) => [
      ...nodes,
      {
        nodeKey,
        label: nodeKey,
        sub: null,
        kind,
        x: point.x,
        y: point.y,
        parentKey: null,
        aliases: [],
      },
    ]);
    this.selectedKey.set(nodeKey);
  }

  onNodePointerDown(event: PointerEvent, node: CyberTopologyNode): void {
    this.flushPendingEdit();
    event.preventDefault();
    event.stopPropagation();

    if (this.linkMode()) {
      // A leaf is drawn hanging off its parent, never as an endpoint, so an edge touching one
      // would be saved and then never rendered. The "Hangs off" field is what attaches it.
      if (node.kind === 'leaf') {
        this.linkNotice.set(
          `${node.label} is a leaf — attach it with “Hangs off”, not a link.`,
        );
        return;
      }
      this.completeLink(node.nodeKey);
      return;
    }

    this.selectedKey.set(node.nodeKey);
    this.dragKey = node.nodeKey;

    const grabbed = this.toAuthorSpace(event);
    this.dragOffset = grabbed ? { x: node.x - grabbed.x, y: node.y - grabbed.y } : { x: 0, y: 0 };

    (event.target as Element).setPointerCapture?.(event.pointerId);
  }

  onCanvasPointerMove(event: PointerEvent): void {
    if (this.dragKey === null) return;
    const point = this.toAuthorSpace(event);
    if (!point) return;

    this.autoScroll(event);

    const key = this.dragKey;
    const x = round2(point.x + this.dragOffset.x);
    const y = round2(point.y + this.dragOffset.y);
    this.nodes.update((nodes) =>
      nodes.map((node) => (node.nodeKey === key ? { ...node, x, y } : node)),
    );
  }

  onCanvasPointerUp(): void {
    this.dragKey = null;
  }

  zoomIn(): void {
    this.zoom.update((z) => Math.min(ZOOM_MAX, z * ZOOM_STEP));
  }

  zoomOut(): void {
    this.zoom.update((z) => Math.max(ZOOM_MIN, z / ZOOM_STEP));
  }

  resetZoom(): void {
    this.zoom.set(ZOOM_MIN);
  }

  /**
   * Drags the viewport along when a node is pulled towards its edge, so a node can be taken
   * somewhere that is not on screen yet without letting go.
   */
  private autoScroll(event: { clientX: number; clientY: number }): void {
    const wrap = this.wrapEl()?.nativeElement;
    if (!wrap) return;

    const bounds = wrap.getBoundingClientRect();
    const step = (near: number, far: number): number => {
      if (near < AUTOSCROLL_MARGIN) return -Math.min(AUTOSCROLL_MAX_STEP, AUTOSCROLL_MARGIN - near);
      if (far < AUTOSCROLL_MARGIN) return Math.min(AUTOSCROLL_MAX_STEP, AUTOSCROLL_MARGIN - far);
      return 0;
    };

    const left = event.clientX - bounds.left;
    const top = event.clientY - bounds.top;
    const dx = step(left, bounds.width - left);
    const dy = step(top, bounds.height - top);

    if (dx !== 0 || dy !== 0) wrap.scrollBy(dx, dy);
  }

  /**
   * Pressing the background clears the selection — or abandons a pending link. This listens
   * for `pointerdown` rather than `click` because a node's press stops only the pointer event:
   * the click the browser synthesises afterwards still bubbles up here, and would undo the
   * selection (or the link source) the node press had just set.
   */
  onCanvasPointerDown(): void {
    this.flushPendingEdit();

    if (this.linkMode()) {
      this.linkFrom.set(null);
      this.linkNotice.set(null);
      return;
    }
    this.selectedKey.set(null);
  }

  toggleLinkMode(): void {
    this.linkMode.update((on) => !on);
    this.linkFrom.set(null);
    this.linkNotice.set(null);
  }

  removeEdge(index: number): void {
    this.edges.update((edges) => edges.filter((_, i) => i !== index));
  }

  private completeLink(nodeKey: string): void {
    this.linkNotice.set(null);

    const from = this.linkFrom();
    if (from === null) {
      this.linkFrom.set(nodeKey);
      return;
    }
    if (from === nodeKey) {
      this.linkFrom.set(null);
      return;
    }

    const exists = this.edges().some(
      (edge) =>
        (edge.sourceKey === from && edge.targetKey === nodeKey) ||
        (edge.sourceKey === nodeKey && edge.targetKey === from),
    );
    if (!exists) {
      this.edges.update((edges) => [
        ...edges,
        { sourceKey: from, targetKey: nodeKey, proto: null },
      ]);
    }
    this.linkFrom.set(null);
  }

  /**
   * Converts a pointer position to author coordinates through the SVG's own screen matrix,
   * so the result stays correct whatever the element's size, padding or scroll offset.
   */
  private toAuthorSpace(event: { clientX: number; clientY: number }): { x: number; y: number } | null {
    const svg = this.svgEl()?.nativeElement;
    if (!svg) return null;

    const matrix = svg.getScreenCTM();
    if (!matrix) return null;

    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const local = point.matrixTransform(matrix.inverse());

    // Deliberately unbounded: the canvas grows to contain whatever the pointer asks for, so
    // a node is never fenced in somewhere the user did not put it.
    const { ix, iy } = this.transform;
    return { x: round2(ix(local.x)), y: round2(iy(local.y)) };
  }

  private uniqueKey(kind: string): string {
    const prefix = kind.slice(0, 3).toUpperCase();
    const taken = new Set(this.nodes().map((node) => node.nodeKey));
    do {
      this.newNodeSeq += 1;
    } while (taken.has(`${prefix}${this.newNodeSeq}`));
    return `${prefix}${this.newNodeSeq}`;
  }

  // ── Node form ────────────────────────────────────────────────────────────

  /**
   * Commits whatever is being typed in the side form.
   *
   * Key and aliases commit on `change`, which the browser fires only when the field loses focus.
   * Both ways of leaving the form take that away: the canvas suppresses the default focus change
   * with `preventDefault`, and clearing the selection removes the field from the DOM before it
   * would have fired. Blurring it here dispatches `change` synchronously, so the edit lands
   * before the thing that would otherwise have discarded it.
   */
  private flushPendingEdit(): void {
    const active = document.activeElement;
    if (
      (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) &&
      this.host.nativeElement.contains(active)
    ) {
      active.blur();
    }
  }

  updateSelected(patch: Partial<CyberTopologyNode>): void {
    const key = this.selectedKey();
    if (key === null) return;

    this.nodes.update((nodes) =>
      nodes.map((node) => (node.nodeKey === key ? { ...node, ...patch } : node)),
    );

    // Edges reference nodes by key, so a rename has to carry through them or the link is
    // silently orphaned and dropped on save.
    if (patch.nodeKey !== undefined && patch.nodeKey !== key) {
      const renamed = patch.nodeKey;
      this.edges.update((edges) =>
        edges.map((edge) => ({
          ...edge,
          sourceKey: edge.sourceKey === key ? renamed : edge.sourceKey,
          targetKey: edge.targetKey === key ? renamed : edge.targetKey,
        })),
      );
      this.nodes.update((nodes) =>
        nodes.map((node) => (node.parentKey === key ? { ...node, parentKey: renamed } : node)),
      );
      this.selectedKey.set(renamed);
    }
  }

  onFieldInput(event: Event, field: 'nodeKey' | 'label' | 'sub'): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (field === 'nodeKey' && !this.acceptKey(value, input)) {
      return;
    }

    this.updateSelected({ [field]: field === 'sub' ? (input.value || null) : value });
  }

  /**
   * Whether a typed key may be applied, putting the field back when it may not.
   *
   * A key that another node already holds would leave two nodes answering to it, and `selected`
   * resolves the form with a `find` — so the form would quietly start editing the other one,
   * which reads exactly like an edit that did not save. The field is reset by hand because the
   * bound value never changed, so Angular has no reason to rewrite it.
   */
  private acceptKey(value: string, input: HTMLInputElement): boolean {
    const current = this.selectedKey();

    if (!value) {
      this.validationError.set('A node needs a key.');
      input.value = current ?? '';
      return false;
    }
    if (this.nodes().some((node) => node.nodeKey === value && node.nodeKey !== current)) {
      this.validationError.set(`Another node already uses the key ${value}.`);
      input.value = current ?? '';
      return false;
    }

    this.validationError.set(null);
    return true;
  }

  onKindChange(event: Event): void {
    const kind = (event.target as HTMLSelectElement).value as CyberNodeKind;
    // A node that stops being a leaf has nothing to hang off any more.
    this.updateSelected({ kind, parentKey: kind === 'leaf' ? this.selected()?.parentKey ?? null : null });
  }

  onParentChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.updateSelected({ parentKey: value === '' ? null : value });
  }

  onAliasesInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.updateSelected({
      aliases: raw
        .split(',')
        .map((alias) => alias.trim())
        .filter((alias) => alias.length > 0),
    });
  }

  onProtoInput(event: Event, index: number): void {
    const value = (event.target as HTMLInputElement).value.trim();
    this.edges.update((edges) =>
      edges.map((edge, i) => (i === index ? { ...edge, proto: value || null } : edge)),
    );
  }

  onNameInput(event: Event): void {
    this.name.set((event.target as HTMLInputElement).value);
  }

  onDescriptionInput(event: Event): void {
    this.description.set((event.target as HTMLTextAreaElement).value);
  }

  deleteSelected(): void {
    this.flushPendingEdit();

    const key = this.selectedKey();
    if (key === null) return;

    this.nodes.update((nodes) =>
      nodes
        .filter((node) => node.nodeKey !== key)
        .map((node) => (node.parentKey === key ? { ...node, parentKey: null } : node)),
    );
    this.edges.update((edges) =>
      edges.filter((edge) => edge.sourceKey !== key && edge.targetKey !== key),
    );
    this.selectedKey.set(null);
  }

  aliasesText(node: CyberTopologyNode): string {
    return node.aliases.join(', ');
  }

  // ── Save ─────────────────────────────────────────────────────────────────

  /**
   * Mirrors the server's rules so the user is told what is wrong before a round trip. The
   * server still enforces them — this is a courtesy, not the guard.
   */
  save(): void {
    // The button press blurs the field on its own, but only because it was a press; this makes
    // the guarantee hold however the button was activated.
    this.flushPendingEdit();

    const name = this.name().trim();
    if (!name) {
      this.validationError.set('Give the topology a name.');
      return;
    }

    const nodes = this.nodes();
    const keys = new Set<string>();
    for (const node of nodes) {
      if (!node.nodeKey.trim()) {
        this.validationError.set('Every node needs a key.');
        return;
      }
      if (keys.has(node.nodeKey)) {
        this.validationError.set(`Duplicate node key: ${node.nodeKey}`);
        return;
      }
      keys.add(node.nodeKey);
    }

    this.validationError.set(null);
    this.saved.emit({
      name,
      description: this.description().trim() || null,
      siteId: this.draft().siteId,
      nodes,
      // A link whose endpoint was deleted is dropped rather than rejected — the user
      // removed the node on purpose, and the dangling line is just fallout.
      edges: this.edges().filter(
        (edge) => keys.has(edge.sourceKey) && keys.has(edge.targetKey),
      ),
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
